import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { patientService } from '@/services/patientService';
import { Patient, PatientData, PatientFilters } from '@/types/patient.types';

// Query keys
const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (filters: PatientFilters) => [...patientKeys.lists(), filters] as const,
  details: () => [...patientKeys.all, 'detail'] as const,
  detail: (id: number) => [...patientKeys.details(), id] as const,
  search: (query: string) => [...patientKeys.all, 'search', query] as const,
  statistics: () => [...patientKeys.all, 'statistics'] as const,
};

// Get patients with pagination
export const usePatients = (filters: PatientFilters = {}) => {
  return useQuery({
    queryKey: patientKeys.list(filters),
    queryFn: () => patientService.getPatients(filters),
    placeholderData: (prev) => prev, // Replaced keepPreviousData in v5
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Get single patient
export const usePatient = (id: number) => {
  return useQuery({
    queryKey: patientKeys.detail(id),
    queryFn: () => patientService.getPatient(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Search patients
export const usePatientSearch = (query: string) => {
  return useQuery({
    queryKey: patientKeys.search(query),
    queryFn: () => patientService.searchPatients(query),
    enabled: query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  });
};

// Get patient statistics
export const usePatientStatistics = () => {
  return useQuery({
    queryKey: patientKeys.statistics(),
    queryFn: () => patientService.getStatistics(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Create patient mutation
export const useCreatePatient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PatientData) => patientService.createPatient(data),
    onSuccess: (newPatient: Patient) => {
      // Invalidate and refetch patient lists
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: patientKeys.statistics() });
      
      // Add the new patient to the cache
      queryClient.setQueryData(patientKeys.detail(newPatient.id), newPatient);
    },
  });
};

// Update patient mutation
export const useUpdatePatient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PatientData }) => 
      patientService.updatePatient(id, data),
    onSuccess: (updatedPatient: Patient) => {
      // Update the patient in the cache
      queryClient.setQueryData(patientKeys.detail(updatedPatient.id), updatedPatient);
      
      // Invalidate lists to ensure they're updated
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
    },
  });
};

// Generate patient code
export const useGeneratePatientCode = () => {
  return useMutation({
    mutationFn: (hospitalId: number) => patientService.generatePatientCode(hospitalId),
  });
};